import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, Input, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { PartsManagementService } from '../../services/parts-management.service';
import { PartsManagementResponse } from '../../models/parts-management-response.model';
import { APICallStatus } from '../../models/api-call-status.mode';
import { AuthPolicies } from '../../auth/auth-policies';
import { PartsManagementHeaderComponent } from '../../components/view-parts-management/parts-management-header/parts-management-header.component';
import { LoaderComponent } from '../../shared-components/loader/loader.component';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import { ReplaySubject, EMPTY, catchError, firstValueFrom, takeUntil } from 'rxjs';
import { ToastrService } from '../../services/toastr-service';
import { ApplicationtTextService } from '../../services/application-text.service';
import { PartsManagementCommentsComponent } from '../../components/view-parts-management/parts-management-comments/parts-management-comments.component';
import { PartsManagementCostDetailsComponent } from '../../components/view-parts-management/parts-management-cost-details/parts-management-cost-details.component';
import { PartsManagementOperationalSettingsComponent } from '../../components/view-parts-management/parts-management-operational-settings/parts-management-operational-settings.component';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';

@Component({
  selector: 'app-view-parts-management',
  imports: [
    CommonModule,
    RouterModule,
    PartsManagementHeaderComponent,
    PartsManagementCommentsComponent,
    PartsManagementOperationalSettingsComponent,
    PartsManagementCostDetailsComponent,
    LoaderComponent,
    MasterDataAuditLogComponent,
  ],
  templateUrl: './view-parts-management.component.html',
  styleUrl: './view-parts-management.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ViewPartsManagementComponent implements OnInit {
  @Input() id!: string;
  partsManagement: PartsManagementResponse | null = null;
  viewDataApiCalls: APICallStatus = 'NOT_STARTED';
  partsManagementStatus = signal('');
  openAuditLog: boolean = false;
  auditlogApiCallStatus: APICallStatus = 'NOT_STARTED';
  lastUpdated: string = '';
  showDetailsScreen: boolean = true;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor(
    private router: Router,
    private toastrService: ToastrService,
    private partsManagementService: PartsManagementService,
    private appTextService: ApplicationtTextService,
    public authPolicy: AuthPolicies,
  ) {}

  async ngOnInit() {
    this.viewDataApiCalls = 'IN_PROGRESS';
    this.partsManagement = await firstValueFrom(
      this.partsManagementService.getPartsManagementById(this.id).pipe(
        takeUntil(this.destroyed$),
        catchError((err) => {
          this.viewDataApiCalls = 'FAILED';
          this.toastrService.showToastr('Unable to fetch parts management details', 'error');
          return EMPTY;
        }),
      ),
    );
    if (this.partsManagement) {
      this.viewDataApiCalls = 'COMPLETED_SUCCESFULLY';
    } else {
      this.toastrService.showToastr('Parts management details not found', 'warning');
      this.viewDataApiCalls = 'FAILED';
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  onEditClicked(): void {
    if (this.partsManagement?.partCode) {
      this.router.navigate(['/master-data/parts-management/edit', this.partsManagement.partCode]);
    }
  }

  onAuditLogClicked() {
    if (!this.partsManagement?.partCode) {
      this.toastrService.showToastr('Part code not available for audit log', 'error');
      return;
    }
    this.openAuditLog = true;
  }

  onAuditLogClosed() {
    this.openAuditLog = false;
  }

  readonly auditLogFetcher = this.partsManagementService.getPartAuditLogs.bind(this.partsManagementService);
}
